/**
 * iFrame Host Script
 *
 * Lightweight script (~3KB) that runs on the WordPress page.
 * Responsibilities:
 * - Creates and positions the iframe
 * - Manages iframe resize (toggle button vs drawer open)
 * - Creates and manages the overlay on the host page
 * - Handles fixed-element adjustments for sidebar mode
 * - Listens for postMessage events from the iframe
 */
import type { IframeToHostMessage } from './scripts/lib/iframe-protocol';
import {
	isIframeToHostMessage,
	MESSAGE_PREFIX,
	sendToIframe,
} from './scripts/lib/iframe-protocol';
import { getIslandIframeSizePx } from './scripts/lib/island-size';
import './styles/iframe-host.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const IFRAME_ID = 'dhsv100-ai-chat-iframe';
const HOST_CONTAINER_ID = 'dhsv100-chat-host';
const OVERLAY_CLASS = 'dhsv100-chat-overlay';
const SIDEBAR_OPEN_CLASS = 'dhsv100-chat-sidebar-open';

// DOM event dispatched on window once window.DhsvChat is installed, so themes
// have a deterministic readiness signal instead of polling for the global.
const DHSV_CHAT_READY_EVENT = 'dhsv100-chat-api-ready';

// How long a command may wait for the iframe app to report 'chat-ready'
// before it is discarded. Measured from the most recent deferral.
const CHAT_READY_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type DrawerVariant = 'default' | 'sidebar' | 'fullscreen';
type DrawerState = 'closed' | DrawerVariant;
type ChatStatus = 'idle' | 'streaming' | 'submitted' | 'error';
type ChatMessage = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	timestamp: number;
};
type OpenWithMessageOptions = {
	variant?: DrawerVariant;
	startNewChat?: boolean;
};
type DhsvChatEvent =
	| 'ready'
	| 'open'
	| 'close'
	| 'message'
	| 'messages-changed'
	| 'status-changed'
	| 'response-done';

interface DhsvChatApi {
	open: ( variant?: DrawerVariant ) => void;
	close: () => void;
	toggle: ( variant?: DrawerVariant ) => void;
	isOpen: () => boolean;
	openWithMessage: (
		text: string,
		options?: DrawerVariant | OpenWithMessageOptions
	) => void;
	openWithNewMessage: ( text: string, variant?: DrawerVariant ) => void;
	sendMessage: ( text: string ) => void;
	newChat: () => void;
	clearHistory: () => void;
	openHistory: () => void;
	getMessages: () => ChatMessage[];
	getStatus: () => ChatStatus;
	on: (
		event: DhsvChatEvent,
		callback: ( payload?: unknown ) => void
	) => void;
	off: (
		event: DhsvChatEvent,
		callback: ( payload?: unknown ) => void
	) => void;
	help: () => void;
}

export type DhsvChatReadyEvent = CustomEvent< { api: DhsvChatApi } >;

declare global {
	interface Window {
		// Optional: absent until init() installs it, and never installed at all
		// on pages without a host container. The truthiness guard themes are
		// told to use only narrows if the type admits undefined.
		DhsvChat?: DhsvChatApi;
	}
	interface WindowEventMap {
		[ DHSV_CHAT_READY_EVENT ]: DhsvChatReadyEvent;
	}
}

let currentState: DrawerState = 'closed';
let iframe: HTMLIFrameElement | null = null;
let overlay: HTMLDivElement | null = null;
let closeFrame: number | null = null;
let closeTimer: number | null = null;
let isScrolled = false;
let isBubbleVisible = true;
let messagesCache: ChatMessage[] = [];
let statusCache: ChatStatus = 'idle';
let previousMessageCount = 0;

// Readiness gate: the iframe app mounts well after the host script runs, and
// only then listens for host commands. Until it reports 'chat-ready', a
// command is held here rather than applied -- expanding the iframe around a
// drawer that cannot open would strand the page.
let isChatReady = false;
let pendingCommand: { label: string; run: () => void } | null = null;
let pendingCommandTimer: ReturnType< typeof setTimeout > | null = null;
const listeners: Map<
	DhsvChatEvent,
	Set< ( payload?: unknown ) => void >
> = new Map();

function emit( event: DhsvChatEvent, payload?: unknown ): void {
	const handlers = listeners.get( event );
	if ( ! handlers ) {
		return;
	}
	handlers.forEach( ( handler ) => {
		handler( payload );
	} );
}

// ---------------------------------------------------------------------------
// Fixed Elements (moved from StickyChat/lib/utils.ts)
// ---------------------------------------------------------------------------

function getFixedElements(): Element[] {
	const allElements = document.querySelectorAll( '*' );
	return Array.from( allElements ).filter( ( element ) => {
		const computedStyle = window.getComputedStyle( element );
		return computedStyle.getPropertyValue( 'position' ) === 'fixed';
	} );
}

function tagFixedElements(): void {
	const fixedElements = getFixedElements();
	fixedElements.forEach( ( element ) => {
		// Don't tag our own elements
		if (
			element.id === IFRAME_ID ||
			element.classList.contains( OVERLAY_CLASS )
		) {
			return;
		}
		element.classList.add( 'fixed-element' );
	} );
}

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

function createOverlay(): HTMLDivElement {
	const el = document.createElement( 'div' );
	el.className = OVERLAY_CLASS;
	el.addEventListener( 'click', () => {
		// Send close request to iframe
		if ( iframe ) {
			sendToIframe( iframe, {
				type: MESSAGE_PREFIX,
				action: 'close-request',
			} );
		}
	} );
	document.body.appendChild( el );
	return el;
}

function showOverlay( variant: DrawerVariant ): void {
	if ( ! overlay ) {
		overlay = createOverlay();
	}
	// Remove previous variant classes
	overlay.classList.remove(
		`${ OVERLAY_CLASS }--default`,
		`${ OVERLAY_CLASS }--sidebar`,
		`${ OVERLAY_CLASS }--fullscreen`
	);
	overlay.classList.add(
		`${ OVERLAY_CLASS }--visible`,
		`${ OVERLAY_CLASS }--${ variant }`
	);
}

function hideOverlay(): void {
	if ( ! overlay ) {
		return;
	}

	// Closing is not animated, so everything comes off in the same frame.
	overlay.classList.remove(
		`${ OVERLAY_CLASS }--visible`,
		`${ OVERLAY_CLASS }--default`,
		`${ OVERLAY_CLASS }--sidebar`,
		`${ OVERLAY_CLASS }--fullscreen`
	);
}

// ---------------------------------------------------------------------------
// iFrame helpers
// ---------------------------------------------------------------------------

function getCollapsedIframeSize(): { width: string; height: string } {
	if ( ! isBubbleVisible ) {
		return { width: '0px', height: '0px' };
	}
	const { width, height } = getIslandIframeSizePx( window.innerWidth );
	return { width: `${ width }px`, height: `${ height }px` };
}

/**
 * Place the collapsed iframe centred along the bottom edge.
 *
 * The pill is horizontally centred, so the iframe is too -- `inset: auto 0 0`
 * plus `margin: auto` would centre it only if the width were fixed, and the
 * width tracks the viewport.
 */
function applyCollapsedPosition(): void {
	if ( ! iframe ) {
		return;
	}
	const size = getCollapsedIframeSize();
	iframe.style.position = 'fixed';
	iframe.style.inset = 'auto auto 0 50%';
	iframe.style.transform = 'translateX(-50%)';
	iframe.style.width = size.width;
	iframe.style.height = size.height;
	iframe.style.pointerEvents = 'auto';
}

/**
 * Expand the iframe to cover the full viewport.
 * This happens INSTANTLY (no CSS transitions) so the vaul drawer
 * animation is the only visual transition the user sees.
 *
 * After expansion, sends an 'expanded' message to the iframe so it
 * knows the viewport is ready and can safely open the drawer.
 */
function expandIframe(): void {
	if ( ! iframe ) {
		return;
	}
	iframe.style.position = 'fixed';
	iframe.style.inset = '0';
	iframe.style.transform = 'none';
	iframe.style.width = '100vw';
	iframe.style.height = '100svh';
	iframe.style.pointerEvents = 'auto';

	// Notify the iframe that expansion is complete.
	// Use rAF to ensure the browser has applied the new dimensions.
	requestAnimationFrame( () => {
		if ( iframe ) {
			sendToIframe( iframe, {
				type: MESSAGE_PREFIX,
				action: 'expanded',
			} );
		}
	} );
}

/**
 * Distance the reader has to travel before the pill compacts to its glyph.
 * Enough that a small nudge, or the bounce at the top of the page, does not
 * trigger it.
 */
const SCROLL_COMPACT_THRESHOLD_PX = 80;

/**
 * Relay the page's scroll state into the iframe.
 *
 * Only the host can observe the page scrolling, but the pill that reacts to it
 * lives inside the iframe. Sent only on a change, so a scroll produces one
 * message rather than one per frame.
 */
function syncScrollState(): void {
	const scrolled = window.scrollY > SCROLL_COMPACT_THRESHOLD_PX;
	if ( scrolled === isScrolled ) {
		return;
	}
	isScrolled = scrolled;

	if ( iframe && isChatReady ) {
		sendToIframe( iframe, {
			type: MESSAGE_PREFIX,
			action: 'scroll-state',
			scrolled,
		} );
	}
}

/** Fallback if the stylesheet has not resolved, matching its own default. */
const CLOSE_ANIMATION_FALLBACK_MS = 580;

/**
 * How long the panel takes to collapse back onto the pill.
 *
 * Read from the stylesheet rather than duplicated here, so the host waits
 * exactly as long as the animation actually runs.
 * @see styles/iframe-content.scss -- --dhsv-morph-close-duration
 */
function getCloseAnimationMs(): number {
	const raw = getComputedStyle( document.documentElement )
		.getPropertyValue( '--dhsv-morph-close-duration' )
		.trim();

	if ( raw.endsWith( 'ms' ) ) {
		return parseFloat( raw ) || CLOSE_ANIMATION_FALLBACK_MS;
	}
	if ( raw.endsWith( 's' ) ) {
		return ( parseFloat( raw ) || 0 ) * 1000 || CLOSE_ANIMATION_FALLBACK_MS;
	}
	return CLOSE_ANIMATION_FALLBACK_MS;
}

/**
 * Shrink the iframe back to the toggle-button size (bottom-right corner).
 * Called AFTER the vaul drawer close animation completes.
 */
function shrinkIframe(): void {
	applyCollapsedPosition();
}

// ---------------------------------------------------------------------------
// iFrame Resize State Machine
// ---------------------------------------------------------------------------

function setIframeState( state: DrawerState ): void {
	if ( ! iframe ) {
		return;
	}

	// Drop a queued shrink -- a reopen must not be collapsed by the frame or
	// timer the preceding close scheduled.
	if ( closeFrame !== null ) {
		cancelAnimationFrame( closeFrame );
		closeFrame = null;
	}
	if ( closeTimer !== null ) {
		clearTimeout( closeTimer );
		closeTimer = null;
	}

	currentState = state;
	emit( state === 'closed' ? 'close' : 'open' );

	switch ( state ) {
		case 'closed':
			// Hide overlay immediately
			hideOverlay();
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );

			// The panel collapses back onto the pill inside the iframe, so the
			// iframe has to stay full-viewport until that has played out --
			// shrinking early would clip the animation to the pill's box. It
			// covers the page in the meantime, but pointer-events are dropped
			// so the page underneath stays usable.
			//
			// The state is re-checked on fire because an immediate reopen
			// would already have expanded the iframe again by then.
			if ( iframe ) {
				iframe.style.pointerEvents = 'none';
			}
			closeTimer = window.setTimeout( () => {
				closeTimer = null;
				if ( currentState === 'closed' ) {
					shrinkIframe();
				}
			}, getCloseAnimationMs() );
			break;

		case 'default':
			// Instantly expand to full viewport -- vaul handles the drawer animation
			expandIframe();
			showOverlay( 'default' );
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			break;

		case 'sidebar':
			// Instantly expand to full viewport -- vaul handles the slide animation
			expandIframe();
			showOverlay( 'sidebar' );
			document.documentElement.classList.add( SIDEBAR_OPEN_CLASS );
			break;

		case 'fullscreen':
			// Instantly expand to full viewport -- vaul handles the slide animation
			expandIframe();
			showOverlay( 'fullscreen' );
			document.documentElement.classList.remove( SIDEBAR_OPEN_CLASS );
			break;
	}
}

// ---------------------------------------------------------------------------
// iFrame Creation
// ---------------------------------------------------------------------------

function createIframe(): HTMLIFrameElement {
	const el = document.createElement( 'iframe' );
	el.id = IFRAME_ID;

	// Build the iframe src URL
	const url = new URL( window.location.href );
	url.searchParams.set( 'dhsv100_chat_iframe', '1' );
	// Remove hash to avoid scrolling issues
	url.hash = '';
	el.src = url.toString();

	// Styling -- starts as the collapsed island pill, centred on the bottom edge.
	// Concrete geometry is applied by applyCollapsedPosition() once the element
	// is assigned to the module-level `iframe`.
	el.style.border = 'none';
	el.style.background = 'transparent';
	el.style.zIndex = '2147483647';
	el.style.colorScheme = 'none';
	// NO transitions -- iframe resizes instantly; vaul provides the animation

	// Permissions
	el.setAttribute( 'allow', 'clipboard-write' );
	el.setAttribute( 'title', 'Chat' );

	return el;
}

// ---------------------------------------------------------------------------
// Message Handler
// ---------------------------------------------------------------------------

function handleMessage( event: MessageEvent ): void {
	// Only accept messages from our iframe
	if ( ! iframe || event.source !== iframe.contentWindow ) {
		return;
	}

	if ( ! isIframeToHostMessage( event.data ) ) {
		return;
	}

	const msg: IframeToHostMessage = event.data;

	switch ( msg.action ) {
		case 'ready':
			// iFrame is loaded and React app is mounted.
			break;

		case 'chat-ready':
			// Idempotent: a repeat 'chat-ready' leaves the flag set and simply
			// finds nothing pending to drain.
			isChatReady = true;
			clearPendingCommandTimer();
			// The page may already be scrolled (a deep link, or a reload part
			// way down), and the pill only learns about it from here.
			if ( iframe && isScrolled ) {
				sendToIframe( iframe, {
					type: MESSAGE_PREFIX,
					action: 'scroll-state',
					scrolled: true,
				} );
			}
			// 'ready' is emitted BEFORE draining, so a subscriber that reveals
			// its launcher on 'ready' and hides it on 'open' sees them in that
			// order -- the replayed command emits 'open' synchronously.
			emit( 'ready' );
			runPendingCommand();
			break;

		case 'drawer-open':
			setIframeState( msg.variant );
			break;

		case 'drawer-close':
			setIframeState( 'closed' );
			break;

		case 'messages-changed':
			messagesCache = msg.messages;
			emit( 'messages-changed', messagesCache );
			if ( messagesCache.length > previousMessageCount ) {
				emit( 'message', messagesCache[ messagesCache.length - 1 ] );
			}
			previousMessageCount = messagesCache.length;
			break;

		case 'status-changed':
			statusCache = msg.status;
			emit( 'status-changed', statusCache );
			break;

		case 'response-done':
			emit( 'response-done' );
			break;
	}
}

function postToIframe(
	message:
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'open-request';
				variant?: DrawerVariant;
		  }
		| { type: typeof MESSAGE_PREFIX; action: 'close-request' }
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'open-with-message';
				text: string;
				variant?: DrawerVariant;
				startNewChat?: boolean;
		  }
		| { type: typeof MESSAGE_PREFIX; action: 'send-message'; text: string }
		| { type: typeof MESSAGE_PREFIX; action: 'new-chat' }
		| { type: typeof MESSAGE_PREFIX; action: 'clear-history' }
		| {
				type: typeof MESSAGE_PREFIX;
				action: 'set-view';
				view: 'popover' | 'history';
		  }
): void {
	if ( ! iframe ) {
		return;
	}
	sendToIframe( iframe, message );
}

// ---------------------------------------------------------------------------
// Readiness Gate
// ---------------------------------------------------------------------------

function clearPendingCommandTimer(): void {
	if ( pendingCommandTimer ) {
		clearTimeout( pendingCommandTimer );
		pendingCommandTimer = null;
	}
}

/**
 * Run a command now if the iframe app is listening, otherwise hold it.
 *
 * Only one command is held: it is always the caller's most recent intent, so a
 * later close() supersedes an earlier open(). Replacement is unconditional and
 * does not inspect the command being replaced. The timeout restarts on each
 * deferral, keeping the deadline anchored to the latest intent.
 *
 * @param {string}   label Command name, used in the timeout warning.
 * @param {Function} run   The command to run now or on 'chat-ready'.
 */
function deferUntilReady( label: string, run: () => void ): void {
	if ( isChatReady ) {
		run();
		return;
	}

	clearPendingCommandTimer();
	pendingCommand = { label, run };
	pendingCommandTimer = setTimeout( () => {
		const discarded = pendingCommand;
		pendingCommand = null;
		pendingCommandTimer = null;
		if ( discarded ) {
			// eslint-disable-next-line no-console
			console.warn(
				'dhsv100-ai-chat-plugin: the chat did not become ready within ' +
					CHAT_READY_TIMEOUT_MS +
					'ms; discarding pending "' +
					discarded.label +
					'" command'
			);
		}
	}, CHAT_READY_TIMEOUT_MS );
}

function runPendingCommand(): void {
	clearPendingCommandTimer();
	const command = pendingCommand;
	pendingCommand = null;
	if ( command ) {
		command.run();
	}
}

function openDrawer( variant: DrawerVariant = 'default' ): void {
	setIframeState( variant );
	postToIframe( { type: MESSAGE_PREFIX, action: 'open-request', variant } );
}

function parseOpenWithMessageOptions(
	options?: DrawerVariant | OpenWithMessageOptions
): Required< OpenWithMessageOptions > {
	if ( ! options ) {
		return { variant: 'default', startNewChat: false };
	}
	if ( typeof options === 'string' ) {
		return { variant: options, startNewChat: false };
	}
	return {
		variant: options.variant || 'default',
		startNewChat: options.startNewChat === true,
	};
}

function printHelp(): void {
	// eslint-disable-next-line no-console
	console.group(
		'%cDhsvChat API Reference',
		'font-weight:bold;font-size:14px;'
	);
	// eslint-disable-next-line no-console
	console.log( 'Drawer Control' );
	// eslint-disable-next-line no-console
	console.log( '  open(variant?)        Open the chat drawer.' );
	// eslint-disable-next-line no-console
	console.log( '  close()               Close the chat drawer.' );
	// eslint-disable-next-line no-console
	console.log( '  toggle(variant?)      Toggle open and close.' );
	// eslint-disable-next-line no-console
	console.log( '  isOpen()              Returns true when open.' );
	// eslint-disable-next-line no-console
	console.log( 'Messaging' );
	// eslint-disable-next-line no-console
	console.log( '  openWithMessage(text, options?)' );
	// eslint-disable-next-line no-console
	console.log(
		"                        Open and submit message. options: { variant?: 'default'|'sidebar'|'fullscreen', startNewChat?: boolean }"
	);
	// eslint-disable-next-line no-console
	console.log( '  openWithNewMessage(text, variant?)' );
	// eslint-disable-next-line no-console
	console.log( '                        Shortcut for startNewChat=true.' );
	// eslint-disable-next-line no-console
	console.log( '  sendMessage(text)     Submit message without opening.' );
	// eslint-disable-next-line no-console
	console.log( 'Chat Lifecycle' );
	// eslint-disable-next-line no-console
	console.log( '  newChat()             Start a new chat.' );
	// eslint-disable-next-line no-console
	console.log( '  clearHistory()        Delete all chat history.' );
	// eslint-disable-next-line no-console
	console.log( '  openHistory()         Open chat history view.' );
	// eslint-disable-next-line no-console
	console.log( 'Read State' );
	// eslint-disable-next-line no-console
	console.log( '  getMessages()         Get cached messages.' );
	// eslint-disable-next-line no-console
	console.log( '  getStatus()           Get current status.' );
	// eslint-disable-next-line no-console
	console.log( 'Events' );
	// eslint-disable-next-line no-console
	console.log( '  on(event, cb), off(event, cb)' );
	// eslint-disable-next-line no-console
	console.log(
		'  Events: ready, open, close, message, messages-changed, status-changed, response-done'
	);
	// eslint-disable-next-line no-console
	console.log(
		"  on('ready', cb) fires immediately if the chat is already ready."
	);
	// eslint-disable-next-line no-console
	console.log( 'DOM Event' );
	// eslint-disable-next-line no-console
	console.log(
		"  window.addEventListener('" +
			DHSV_CHAT_READY_EVENT +
			"', e => e.detail.api)"
	);
	// eslint-disable-next-line no-console
	console.log(
		'                        Fires once when this API is installed. See docs/guides/custom-launcher.md'
	);
	// eslint-disable-next-line no-console
	console.groupEnd();
}

function registerApi(): void {
	window.DhsvChat = {
		open: ( variant = 'default' ) => {
			deferUntilReady( 'open', () => {
				openDrawer( variant );
			} );
		},
		close: () => {
			deferUntilReady( 'close', () => {
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'close-request',
				} );
			} );
		},
		toggle: ( variant = 'default' ) => {
			deferUntilReady( 'toggle', () => {
				if ( currentState === 'closed' ) {
					openDrawer( variant );
					return;
				}
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'close-request',
				} );
			} );
		},
		isOpen: () => currentState !== 'closed',
		openWithMessage: ( text, options ) => {
			// Validation runs at call time, ahead of the gate: a blank-text
			// no-op must not occupy the pending slot and evict a real command.
			if ( ! text.trim() ) {
				return;
			}
			const parsed = parseOpenWithMessageOptions( options );
			deferUntilReady( 'openWithMessage', () => {
				setIframeState( parsed.variant );
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'open-with-message',
					text,
					variant: parsed.variant,
					startNewChat: parsed.startNewChat,
				} );
			} );
		},
		openWithNewMessage: ( text, variant = 'default' ) => {
			if ( ! text.trim() ) {
				return;
			}
			deferUntilReady( 'openWithNewMessage', () => {
				setIframeState( variant );
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'open-with-message',
					text,
					variant,
					startNewChat: true,
				} );
			} );
		},
		sendMessage: ( text ) => {
			if ( ! text.trim() ) {
				return;
			}
			deferUntilReady( 'sendMessage', () => {
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'send-message',
					text,
				} );
			} );
		},
		newChat: () => {
			deferUntilReady( 'newChat', () => {
				postToIframe( { type: MESSAGE_PREFIX, action: 'new-chat' } );
			} );
		},
		clearHistory: () => {
			// The cache reset happens at call time even while deferred:
			// getMessages() is ungated, so it must never hand back history the
			// caller has already cleared. Only the post is deferred.
			messagesCache = [];
			previousMessageCount = 0;
			deferUntilReady( 'clearHistory', () => {
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'clear-history',
				} );
			} );
		},
		openHistory: () => {
			deferUntilReady( 'openHistory', () => {
				openDrawer();
				postToIframe( {
					type: MESSAGE_PREFIX,
					action: 'set-view',
					view: 'history',
				} );
			} );
		},
		getMessages: () => messagesCache,
		getStatus: () => statusCache,
		on: ( event, callback ) => {
			const set = listeners.get( event ) || new Set();
			set.add( callback );
			listeners.set( event, set );
			// 'ready' is emitted once. A theme module that loads late would
			// otherwise wait forever for an event that has already passed, so
			// replay it -- as a resolved promise still calls .then().
			if ( event === 'ready' && isChatReady ) {
				callback();
			}
		},
		off: ( event, callback ) => {
			const set = listeners.get( event );
			if ( ! set ) {
				return;
			}
			set.delete( callback );
		},
		help: printHelp,
	};
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

function init(): void {
	const hostContainer = document.getElementById( HOST_CONTAINER_ID );
	if ( ! hostContainer ) {
		// eslint-disable-next-line no-console
		console.warn(
			'dhsv100-ai-chat-plugin: Host container #' +
				HOST_CONTAINER_ID +
				' not found'
		);
		return;
	}

	isBubbleVisible =
		hostContainer.getAttribute( 'data-show-bubble' ) !== 'false';

	// Tag fixed elements on the page (for sidebar mode transitions)
	window.addEventListener( 'load', tagFixedElements );

	// Create iframe, then size and place it as the collapsed pill.
	iframe = createIframe();
	hostContainer.appendChild( iframe );
	applyCollapsedPosition();

	// The pill width tracks the viewport, so the iframe has to follow it.
	// Only while collapsed -- an expanded iframe is already full-viewport.
	window.addEventListener( 'resize', () => {
		if ( currentState === 'closed' ) {
			applyCollapsedPosition();
		}
	} );

	// The pill compacts to its glyph once the reader scrolls past the fold.
	// Passive: this must never delay the page's own scrolling.
	//
	// Seeded first, so a page that loads already scrolled (a deep link, or a
	// reload part way down) starts in the right state -- the listener only
	// fires on a change, and the iframe is told once it reports ready.
	isScrolled = window.scrollY > SCROLL_COMPACT_THRESHOLD_PX;
	window.addEventListener( 'scroll', syncScrollState, { passive: true } );

	// Listen for messages from iframe
	window.addEventListener( 'message', handleMessage );
	registerApi();

	// Announce the API in the same synchronous turn as the assignment above, so
	// a listener registered earlier and a window.DhsvChat check made later can
	// never both miss it. Placed after the early return, so its absence means
	// the chat is unavailable on this page.
	window.dispatchEvent(
		new CustomEvent( DHSV_CHAT_READY_EVENT, {
			detail: { api: window.DhsvChat as DhsvChatApi },
			bubbles: false,
			cancelable: false,
		} )
	);
}

// Run
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
